import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react'
import { Button, FormField } from '../common'
import { mediaIngestApi } from '@/services/api'
import { detectExternalSource } from './externalSource'
import styles from './MediaExternalImportPanel.module.css'

// Stable error-code → user copy mapping. The backend returns these codes
// verbatim; keep this in sync with media_ingest_handler.go.
const ERROR_COPY = {
  url_required: 'Please paste a URL first.',
  url_invalid: 'That does not look like a valid URL — try pasting the full link.',
  rights_not_confirmed: 'Confirm you have the right to publish this content before importing.',
  unsupported_platform: 'We do not support imports from this platform yet.',
  external_import_disabled: 'External imports are disabled for this organization. An admin can enable them in Settings.',
  live_stream_not_supported: 'Live streams cannot be imported. Try a recorded video instead.',
  source_too_long: 'This video is too long to import. Shorter clips only, please.',
  source_too_large: 'This video is too large to import.',
  platform_requires_auth: 'This platform requires an authenticated session we do not have. Try uploading the file manually.',
  adapter_broken: 'The importer for this platform needs attention. Upload the file directly for now.',
  unsupported_url: 'This link is not a video we can import. Paste the link to the video itself.',
  source_unavailable: 'This video is no longer available. It may have been removed.',
  download_failed: 'The download failed. Try again in a minute or two.',
  resolve_failed: 'Could not read the source. Double-check the URL.',
}

function copyForError(code) {
  return ERROR_COPY[code] || 'Something went wrong during import. Try again later.'
}

/**
 * MediaExternalImportPanel drives the "Import from URL" experience on the
 * Create Post form. The parent owns the current form state and passes in
 * callbacks for attaching the ingested asset and optionally replacing the
 * post text with the imported caption.
 */
export default function MediaExternalImportPanel({
  orgSlug,
  onImported,
  onSuggestCaption,
  postTextEmpty = true,
  ingestApi = mediaIngestApi,
}) {
  const [url, setUrl] = useState('')
  const [rightsConfirmed, setRightsConfirmed] = useState(false)
  const [rightsNote, setRightsNote] = useState('')
  const [assetId, setAssetId] = useState(null)
  const [preview, setPreview] = useState(null)
  const [captionPromptDismissed, setCaptionPromptDismissed] = useState(false)
  const importedAssetIdRef = useRef(null)

  const detectedPlatform = detectExternalSource(url)

  const startMutation = useMutation({
    mutationFn: async () => {
      return ingestApi.start(orgSlug, {
        url: url.trim(),
        rights_confirmed: rightsConfirmed,
        rights_note: rightsNote.trim(),
      })
    },
    onSuccess: (data) => {
      setAssetId(data.asset_id)
      setPreview(data.preview || null)
      setCaptionPromptDismissed(false)
    },
  })

  const errorCode = useMemo(() => {
    if (!startMutation.isError) return null
    const data = startMutation.error?.response?.data
    return data?.error || 'resolve_failed'
  }, [startMutation.isError, startMutation.error])

  const statusQuery = useQuery({
    queryKey: ['mediaIngest', orgSlug, assetId],
    queryFn: () => ingestApi.getStatus(orgSlug, assetId),
    enabled: Boolean(orgSlug && assetId),
    refetchInterval: (query) => {
      const data = query.state.data
      if (!data) return 3000
      if (data.status === 'ready' || data.status === 'ingest_failed') return false
      return 3000
    },
  })

  const status = statusQuery.data?.status
  const asset = statusQuery.data?.asset
  const failureCode = statusQuery.data?.error?.code

  useEffect(() => {
    if (status === 'ready' && asset?.id && importedAssetIdRef.current !== asset.id) {
      importedAssetIdRef.current = asset.id
      onImported?.({
        id: asset.id,
        asset_type: asset.asset_type,
        url: asset.url,
        object_key: asset.object_key,
        source_object_key: asset.source_object_key,
        title: asset.title,
      })
    }
  }, [status, asset, onImported])

  const isSubmitting = startMutation.isPending
  const isPolling = Boolean(assetId) && status !== 'ready' && status !== 'ingest_failed'
  const canSubmit =
    Boolean(detectedPlatform) && rightsConfirmed && !isSubmitting && !isPolling && !assetId

  const handleReset = () => {
    setUrl('')
    setRightsConfirmed(false)
    setRightsNote('')
    setAssetId(null)
    setPreview(null)
    setCaptionPromptDismissed(false)
    importedAssetIdRef.current = null
    startMutation.reset()
  }

  const showCaptionPrompt =
    status === 'ready' &&
    !captionPromptDismissed &&
    postTextEmpty &&
    preview?.caption &&
    preview.caption.trim().length > 0

  return (
    <div className={styles.panel}>
      <FormField
        label="Source URL"
        placeholder="https://www.youtube.com/watch?v=... or Instagram / TikTok / Drive / X"
        value={url}
        onChange={(event) => setUrl(event.target.value)}
        disabled={Boolean(assetId)}
        help={
          detectedPlatform
            ? `Detected source: ${detectedPlatform}.`
            : 'Paste a link to a public video. We will download, transcode, and attach it to the post.'
        }
      />

      <label className={styles.rightsRow}>
        <input
          type="checkbox"
          checked={rightsConfirmed}
          onChange={(event) => setRightsConfirmed(event.target.checked)}
          disabled={Boolean(assetId)}
          data-testid="ingest-rights-checkbox"
        />
        <span>I confirm I have the right to publish this content.</span>
      </label>

      <FormField
        label="Rights note (optional)"
        type="textarea"
        rows={2}
        value={rightsNote}
        onChange={(event) => setRightsNote(event.target.value)}
        placeholder="e.g. licensed under X, owned by org"
        disabled={Boolean(assetId)}
      />

      <div className={styles.actionRow}>
        <Button
          type="button"
          onClick={() => startMutation.mutate()}
          disabled={!canSubmit}
          data-testid="ingest-import-button"
        >
          {isSubmitting ? 'Importing…' : 'Import'}
        </Button>
        {assetId ? (
          <Button type="button" variant="ghost" onClick={handleReset}>
            Cancel import
          </Button>
        ) : null}
      </div>

      {errorCode ? (
        <div className={styles.errorBanner} role="alert" data-testid="ingest-error">
          <AlertCircle size={16} />
          <div>
            <strong>Import failed</strong>
            <p>{copyForError(errorCode)}</p>
          </div>
        </div>
      ) : null}

      {preview && assetId ? (
        <div className={styles.previewCard} data-testid="ingest-preview">
          {preview.thumbnail_url ? (
            <img src={preview.thumbnail_url} alt="" className={styles.previewThumb} />
          ) : null}
          <div className={styles.previewBody}>
            <strong>{preview.title || 'Imported video'}</strong>
            {preview.duration_sec ? (
              <span>{Math.round(preview.duration_sec)} sec</span>
            ) : null}
            <div className={styles.statusRow}>
              {isPolling ? (
                <>
                  <Loader2 size={14} className={styles.spinner} />
                  <span>
                    {status === 'transcoding'
                      ? 'Transcoding — this usually takes under a minute…'
                      : 'Downloading…'}
                  </span>
                </>
              ) : status === 'ready' ? (
                <>
                  <CheckCircle2 size={14} />
                  <span>Attached to your post.</span>
                </>
              ) : status === 'ingest_failed' ? (
                <>
                  <AlertCircle size={14} />
                  <span>{copyForError(failureCode)}</span>
                </>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}

      {showCaptionPrompt ? (
        <div className={styles.captionPrompt} data-testid="ingest-caption-prompt">
          <span>Use imported caption as post text?</span>
          <div>
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                onSuggestCaption?.(preview.caption)
                setCaptionPromptDismissed(true)
              }}
            >
              Apply
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setCaptionPromptDismissed(true)}
            >
              Dismiss
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  )
}
